<template>
  <div class="orangehrm-late-schedule-wrap">
    <oxd-text tag="h6" class="orangehrm-main-title">
      {{ pt('payroll.late_deduction_schedule') }}
    </oxd-text>
    <oxd-divider />
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="localConfig.graceEndMinutes"
            :label="pt('payroll.late_grace_minutes')"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="localConfig.halfDayMinutesLate"
            :label="pt('payroll.late_half_day_minutes')"
            required
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <div v-if="showBands" class="orangehrm-ter-group">
      <div class="orangehrm-ter-table-wrap">
        <table class="orangehrm-ter-table">
          <thead>
            <tr>
              <th>{{ pt('payroll.late_from_minutes') }}</th>
              <th>{{ pt('payroll.late_to_minutes') }}</th>
              <th>{{ valueColumnLabel }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in localConfig.bands" :key="index">
              <td>
                <input
                  :value="row.fromMinutesLateExclusive"
                  type="text"
                  inputmode="numeric"
                  class="orangehrm-ter-input"
                  @input="onBandInput($event, row, 'fromMinutesLateExclusive')"
                />
              </td>
              <td>
                <input
                  :value="row.toMinutesLateInclusive"
                  type="text"
                  inputmode="numeric"
                  class="orangehrm-ter-input"
                  @input="onBandInput($event, row, 'toMinutesLateInclusive')"
                />
              </td>
              <td>
                <input
                  :value="bandValueDisplay(row)"
                  type="text"
                  inputmode="decimal"
                  class="orangehrm-ter-input"
                  @input="onBandValueInput($event, row)"
                  @blur="onBandValueBlur(row)"
                />
              </td>
              <td class="orangehrm-ter-actions">
                <oxd-icon-button
                  name="trash"
                  class="orangehrm-ter-remove-btn"
                  @click="removeRow(index)"
                />
              </td>
            </tr>
            <tr v-if="localConfig.bands.length === 0">
              <td colspan="4" class="orangehrm-ter-empty">
                {{ $t('general.no_records_found') }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="orangehrm-ter-table-footer">
                <div class="orangehrm-ter-table-footer__actions">
                  <oxd-button
                    display-type="ghost"
                    :label="$t('general.clear')"
                    class="orangehrm-ter-footer-btn"
                    :disabled="localConfig.bands.length === 0"
                    @click="clearBands"
                  />
                  <oxd-button
                    display-type="secondary"
                    :label="$t('general.add')"
                    class="orangehrm-ter-footer-btn"
                    @click="addRow"
                  />
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import {payrollT} from '@/orangehrmPayrollPlugin/util/payrollI18n';

const defaultConfig = () => ({
  graceEndMinutes: 30,
  halfDayMinutesLate: 121,
  bands: [
    {fromMinutesLateExclusive: 0, toMinutesLateInclusive: 30, value: 0},
    {fromMinutesLateExclusive: 30, toMinutesLateInclusive: 120, value: 0.05},
  ],
});

export default {
  name: 'LateDeductionScheduleEditor',
  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    isPercentage: {
      type: Boolean,
      default: true,
    },
    showBands: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      localConfig: this.normalize(this.modelValue),
    };
  },
  computed: {
    valueColumnLabel() {
      return this.isPercentage
        ? this.pt('payroll.late_band_rate')
        : this.pt('payroll.late_band_amount');
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        if (this.configsMatch(val)) {
          return;
        }
        this.localConfig = this.normalize(val);
      },
    },
    'localConfig.graceEndMinutes'() {
      this.emitUpdate();
    },
    'localConfig.halfDayMinutesLate'() {
      this.emitUpdate();
    },
  },
  methods: {
    pt(key) {
      return payrollT(this.$t, key);
    },
    onBandInput(event, row, field) {
      const parsed = parseInt(
        String(event.target.value).replace(/\D/g, ''),
        10,
      );
      row[field] = Number.isFinite(parsed) ? parsed : 0;
      this.emitUpdate();
    },
    bandValueDisplay(row) {
      if (typeof row.valueText === 'string') {
        return row.valueText;
      }
      return this.formatPlainDecimal(row.value);
    },
    formatPlainDecimal(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '';
      }
      return String(number);
    },
    sanitizeDecimalInput(raw) {
      let value = String(raw ?? '').replace(/[^\d.,-]/g, '');
      const negative = value.startsWith('-');
      value = value.replace(/-/g, '');
      const separatorIndex = value.search(/[.,]/);
      if (separatorIndex === -1) {
        return `${negative ? '-' : ''}${value}`;
      }
      const separator = value[separatorIndex];
      const integerPart = value.slice(0, separatorIndex).replace(/[.,]/g, '');
      const fractionPart = value.slice(separatorIndex + 1).replace(/[.,]/g, '');
      return `${negative ? '-' : ''}${integerPart}${separator}${fractionPart}`;
    },
    parseDecimalInput(raw) {
      const value = String(raw ?? '').trim();
      if (
        !value ||
        value === '-' ||
        value === '.' ||
        value === ',' ||
        value === '-.' ||
        value === '-,'
      ) {
        return null;
      }
      const parsed = Number(value.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    },
    onBandValueInput(event, row) {
      const sanitized = this.sanitizeDecimalInput(event?.target?.value);
      row.valueText = sanitized;
      const parsed = this.parseDecimalInput(sanitized);
      if (parsed !== null) {
        row.value = parsed;
      } else if (sanitized === '' || sanitized === '-') {
        row.value = 0;
      }
      this.emitUpdate();
    },
    onBandValueBlur(row) {
      const parsed = this.parseDecimalInput(row.valueText);
      row.value = parsed ?? 0;
      row.valueText = this.formatPlainDecimal(row.value);
      this.emitUpdate();
    },
    configsMatch(raw) {
      const incoming = raw || {};
      if (
        (parseInt(this.localConfig.graceEndMinutes, 10) || 0) !==
        (parseInt(incoming.graceEndMinutes, 10) || 0)
      ) {
        return false;
      }
      if (
        (parseInt(this.localConfig.halfDayMinutesLate, 10) || 0) !==
        (parseInt(incoming.halfDayMinutesLate, 10) || 0)
      ) {
        return false;
      }
      const currentBands = this.localConfig.bands || [];
      const incomingBands = Array.isArray(incoming.bands) ? incoming.bands : [];
      if (currentBands.length !== incomingBands.length) {
        return false;
      }
      return currentBands.every((row, index) => {
        const other = incomingBands[index] || {};
        return (
          (parseInt(row.fromMinutesLateExclusive, 10) || 0) ===
            (parseInt(other.fromMinutesLateExclusive, 10) || 0) &&
          (parseInt(row.toMinutesLateInclusive, 10) || 0) ===
            (parseInt(other.toMinutesLateInclusive, 10) || 0) &&
          Number(row.value) === Number(other.value ?? other.percentOfBase ?? 0)
        );
      });
    },
    normalize(raw) {
      const base = defaultConfig();
      const merged = {...base, ...(raw || {})};
      merged.graceEndMinutes = Number(merged.graceEndMinutes) || 30;
      merged.halfDayMinutesLate = Number(merged.halfDayMinutesLate) || 121;
      delete merged.shiftStartTime;
      merged.bands = Array.isArray(merged.bands)
        ? merged.bands.map((row) => {
            const value = Number(row.value ?? row.percentOfBase ?? 0);
            return {
              fromMinutesLateExclusive: Number(
                row.fromMinutesLateExclusive ?? 0,
              ),
              toMinutesLateInclusive: Number(row.toMinutesLateInclusive ?? 0),
              value,
              valueText: this.formatPlainDecimal(value),
            };
          })
        : base.bands.map((row) => ({
            ...row,
            valueText: this.formatPlainDecimal(row.value),
          }));
      return merged;
    },
    emitUpdate() {
      // Preserve keys owned by the parent form (payGradeIds,
      // payGradeNominalSource, ...) instead of replacing the whole config.
      const payload = {
        ...this.localConfig,
        graceEndMinutes: parseInt(this.localConfig.graceEndMinutes, 10) || 0,
        halfDayMinutesLate:
          parseInt(this.localConfig.halfDayMinutesLate, 10) || 0,
        bands: (this.localConfig.bands || []).map((row) => ({
          fromMinutesLateExclusive:
            parseInt(row.fromMinutesLateExclusive, 10) || 0,
          toMinutesLateInclusive: parseInt(row.toMinutesLateInclusive, 10) || 0,
          value: Number(row.value) || 0,
        })),
      };
      this.$emit('update:modelValue', payload);
    },
    addRow() {
      this.localConfig.bands.push({
        fromMinutesLateExclusive: 0,
        toMinutesLateInclusive: 0,
        value: 0,
        valueText: '0',
      });
      this.emitUpdate();
    },
    removeRow(index) {
      this.localConfig.bands.splice(index, 1);
      this.emitUpdate();
    },
    clearBands() {
      this.localConfig.bands = [];
      this.emitUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-late-schedule-wrap {
  :deep(.oxd-divider) {
    margin-bottom: 1rem;
  }
}

.orangehrm-ter-group {
  margin-top: 1rem;
  margin-bottom: 0;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.orangehrm-ter-table-footer {
  padding: 0.5rem 0.625rem;
  background: #fafbfd;
  vertical-align: middle;
}

.orangehrm-ter-table-footer__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.orangehrm-ter-footer-btn {
  min-width: 100px;
}

.orangehrm-ter-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.8125rem;
  line-height: 1.35;
}

.orangehrm-ter-table-wrap {
  overflow-x: hidden;
}

.orangehrm-ter-table th,
.orangehrm-ter-table td {
  border: 1px solid #edf1f7;
  padding: 0.5rem 0.625rem;
  box-sizing: border-box;
  vertical-align: middle;
  overflow: hidden;
}

.orangehrm-ter-table th {
  background: #fcfdff;
  color: #526887;
  font-weight: 600;
  font-size: 0.75rem;
  text-align: center;
  font-family: sans-serif;
}

.orangehrm-ter-table th:nth-child(4),
.orangehrm-ter-table td:nth-child(4) {
  width: 58px;
}

.orangehrm-ter-table td {
  background: #fff;
}

.orangehrm-ter-table tfoot td {
  background: #fafbfd;
}

.orangehrm-ter-input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: none;
  border-radius: 6px;
  padding: 0.25rem 0;
  font-size: inherit;
  line-height: 1.35;
  color: #26344d;
  background: #fff;
}

.orangehrm-ter-input:focus {
  outline: none;
  box-shadow: none;
}

.orangehrm-ter-actions {
  text-align: center;
}

.orangehrm-ter-remove-btn {
  color: #d86c5b !important;
}

.orangehrm-ter-empty {
  text-align: center;
  color: #64728c;
  padding: 0.75rem 0.5rem;
  font-size: 0.8125rem;
  font-style: italic;
}
</style>
